import React, { useMemo } from 'react';

import {
  CheckCircleFilled,
  CommentOutlined,
  EllipsisOutlined,
  LikeOutlined,
  SendOutlined,
  ShareAltOutlined,
  UserOutlined,
} from '@ant-design/icons';
import { Avatar } from 'antd';
import Linkify from 'linkify-react';
import { useTranslation } from 'react-i18next';

import UserPlaceHolderImage from 'res/img/user-placeholder.png';
import { SocialPostAttachment } from 'types/AmplifiedTemplate';
import { UserProfileSummary } from 'types/store/GetUserById';
import { linkifyOptions } from 'consts/common';
import { HighlightSegment } from 'routes/Advocate/PostModal/LeftInput/PostContentEditor/writePostContentMentions.utils';
import { MentionedUserMeta } from 'types/store/LinkedIn';

import AttachmentPreview from './AttachmentPreview';
import { isAttachmentPreviewable } from './isAttachmentPreviewable';
import buildPostContentSegments from '../../../../utils/buildPostContentSegments';

interface LinkedInPreviewProps {
  previewContent?: string;
  previewCta: string;
  previewHashtags: string;
  attachments: SocialPostAttachment[];
  showActions?: boolean;
  containerClassName?: string;
  getLinkedInInfo?: UserProfileSummary;
  highlightedSegments?: HighlightSegment[];
  /** Raw mention metadata — used to build segments when highlightedSegments is not provided (e.g. selector mode) */
  mentions?: MentionedUserMeta[];
}

const LinkedInPreview = ({
  previewContent,
  previewCta,
  previewHashtags,
  attachments,
  showActions = true,
  containerClassName,
  getLinkedInInfo,
  highlightedSegments,
  mentions,
}: LinkedInPreviewProps) => {
  const { t } = useTranslation();

  const previewableAttachments = attachments.filter((att) =>
    isAttachmentPreviewable(att.attachment),
  );
  const hasMedia = previewableAttachments.length > 0;

  // Build display segments from raw mentions when highlightedSegments is not provided
  const displaySegments = useMemo(() => {
    if (highlightedSegments && highlightedSegments.length > 0) {
      return highlightedSegments;
    }
    if (mentions?.length && previewContent) {
      return buildPostContentSegments(previewContent, mentions);
    }
    return null;
  }, [highlightedSegments, mentions, previewContent]);

  return (
    <div
      className={
        containerClassName ||
        'bg-white rounded-xl shadow-sm border border-str100 p-4 mb-6'
      }
    >
      {/* LinkedIn Header */}
      <div className="flex items-start justify-between mb-3">
        <div className="flex gap-2">
          <Avatar
            size={48}
            icon={<UserOutlined />}
            src={getLinkedInInfo?.linkedin_avatar ?? UserPlaceHolderImage}
          />
          <div>
            <div className="flex items-center gap-1">
              <span
                className="text-sm font-bold text-black"
                title={getLinkedInInfo?.linkedin_name ?? t('Your Name')}
              >
                {getLinkedInInfo?.linkedin_name ?? t('Your Name')}
              </span>
              <CheckCircleFilled className="text-xs text-gray" />
            </div>
            <p
              className="text-xs text-gray leading-tight"
              title={getLinkedInInfo?.linkedin_headline ?? t('Your Role')}
            >
              {getLinkedInInfo?.linkedin_headline ?? t('Your Role')}
            </p>
            {showActions && (
              <p className="text-xs text-gray flex items-center gap-1">
                {t('1w •')}{' '}
                <span className="w-3 h-3 border border-gray rounded-full text-xs flex items-center justify-center">
                  🌐
                </span>
              </p>
            )}
          </div>
        </div>
        {showActions && (
          <EllipsisOutlined className="text-gray text-lg cursor-pointer" />
        )}
      </div>

      {/* LinkedIn Body */}
      <div
        className="text-sm text-black mb-3 leading-snug"
        data-testid="post-preview"
      >
        <p className="mb-2 whitespace-pre-line break-words">
          <Linkify options={linkifyOptions}>
            {displaySegments && displaySegments.length > 0
              ? displaySegments.map((segment, index) => (
                  <span
                    key={`${index}-${segment.isMention ? 'mention' : 'plain'}`}
                    className={
                      segment.isMention ? 'font-medium text-link1' : ''
                    }
                  >
                    {segment.text}
                  </span>
                ))
              : previewContent}
          </Linkify>
        </p>
        {previewCta && (
          <p className="mb-1 text-sm text-black break-all">
            <Linkify options={linkifyOptions}>{previewCta}</Linkify>
          </p>
        )}
        <p className="!font-sfPro text-blue200 text-sm leading-5 !font-medium">
          {previewHashtags}
        </p>
      </div>

      {/* LinkedIn Media */}
      {hasMedia && (
        <div className="w-full relative rounded-md overflow-hidden cursor-pointer">
          <AttachmentPreview
            isPostPreview
            attachments={previewableAttachments}
          />
        </div>
      )}

      {showActions && (
        <div className="flex items-center justify-between pt-2 border-t border-str50 mt-4">
          <div className="flex items-center gap-1 text-gray hover:text-primary transition-colors cursor-pointer px-2 py-1 rounded hover:bg-bg100">
            <LikeOutlined className="text-lg" />
            <span className="text-xs font-medium">{t('Like')}</span>
          </div>
          <div className="flex items-center gap-1 text-gray hover:text-primary transition-colors cursor-pointer px-2 py-1 rounded hover:bg-bg100">
            <CommentOutlined className="text-lg" />
            <span className="text-xs font-medium">{t('Comment')}</span>
          </div>
          <div className="flex items-center gap-1 text-gray hover:text-primary transition-colors cursor-pointer px-2 py-1 rounded hover:bg-bg100">
            <ShareAltOutlined className="text-lg" />
            <span className="text-xs font-medium">{t('Share')}</span>
          </div>
          <div className="flex items-center gap-1 text-gray hover:text-primary transition-colors cursor-pointer px-2 py-1 rounded hover:bg-bg100">
            <SendOutlined className="text-lg" />
            <span className="text-xs font-medium">{t('Send')}</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default LinkedInPreview;
